/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { FileType } from "Api";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { QuickFormDropZone } from "./QuickFormDropZone";
import { QuickFormActionBar } from "./QuickFormActionBar";
import { QuickFormTable } from "./QuickFormTable";
import { QuickFormPagination } from "./QuickFormPagination";
import { QuickFormErrorModal } from "./QuickFormErrorModal";
import { QuickFormState, QuickFormReducer } from "./QuickFormState";
import { TransformStatus, UploadStatus } from "./QuickFormTypes";
import { AxiosContext } from "../Utils/AuthContext";
import { loggedNotify, Component } from "../Shared/Notify";
import { Endpoints } from "../Utils/Common";
import { processFilesWithZipExtraction } from "./QuickFormZipUtils";
import { ruleEngine } from "./QuickFormValidationRules";

export const QuickForm = (): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const axiosContext = React.useContext(AxiosContext);

  const [state, dispatch] = React.useReducer(
    QuickFormReducer,
    new QuickFormState()
  );

  const sessionId = React.useMemo(
    () => `quickform-${Date.now()}-${Math.random().toString(36)}`,
    []
  );

  const [errorModalState, setErrorModalState] = React.useState<{
    isOpen: boolean;
    fileName: string;
    errorMessage: string;
    fileId: string;
  }>({
    isOpen: false,
    fileName: "",
    errorMessage: "",
    fileId: "",
  });

  // Calculate pagination
  const totalPages = Math.ceil(state.files.length / state.itemsPerPage);
  const paginatedFiles = state.files.slice(
    (state.currentPage - 1) * state.itemsPerPage,
    state.currentPage * state.itemsPerPage
  );

  // Keep currentPage valid whenever the file list shrinks (e.g. bulk delete
  // wipes out the page we were viewing), so new uploads are visible again.
  React.useEffect(() => {
    const validTotalPages = Math.max(
      1,
      Math.ceil(state.files.length / state.itemsPerPage)
    );
    if (state.currentPage > validTotalPages) {
      dispatch(["setPage", { page: validTotalPages }]);
    }
  }, [state.files.length, state.itemsPerPage, state.currentPage]);

  // Handle file upload
  const handleUpload = async (files: File[]): Promise<void> => {
    try {
      // Extract any zip files before processing
      const processedFiles = await processFilesWithZipExtraction(files);

      if (processedFiles.length === 0) {
        if (processedFiles.length === 0) {
          loggedNotify.warning(
            axiosContext,
            Component.QUICKFORM,
            "No files to upload"
          );
          return;
        }
        return;
      }

      // STEP 1: Add files to state immediately with UPLOADING status
      const pendingFiles = processedFiles.map((file) => ({
        id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID
        fileInfo: {
          id: `temp-${Date.now()}-${Math.random()}`,
          attributes: {
            name: file.name,
            sizeMbs: file.size / (1024 * 1024),
          },
        },
        detectedType: null, // Not detected yet, we do this after upload
        uploadStatus: UploadStatus.UPLOADING,
        uploadProgress: 0,
        transformStatus: TransformStatus.PENDING,
        transformTo: null,
        selected: false,
        sizeMbs: file.size / (1024 * 1024),
      }));

      dispatch(["addQuickFormFiles", { files: pendingFiles }]);

      // STEP 2: Upload files with progress tracking
      const formData = new FormData();
      processedFiles.forEach((f) => formData.append("files", f));

      const response = await axiosContext.post(
        Endpoints.QuickFormUpload,
        formData,
        {
          headers: { "X-Session-ID": sessionId },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            // Update all uploading files with progress
            pendingFiles.forEach((file) => {
              dispatch([
                "updateFileUploadProgress",
                { fileId: file.id, progress },
              ]);
            });
          },
        }
      );

      // STEP 3: Upload complete - update with real IDs
      const uploadedFiles = response.data.map((file: any, index: number) => ({
        ...pendingFiles[index],
        id: file.id, // Real ID from backend
        fileInfo: {
          ...pendingFiles[index].fileInfo,
          id: file.id,
          attributes: {
            name: file.name,
            sizeMbs: file.sizeMbs,
            uploadDateUtc: new Date(file.uploadedAt).toISOString(),
          },
        },
        uploadStatus: UploadStatus.COMPLETE,
        uploadProgress: 100,
        sizeMbs: file.sizeMbs,
      }));

      // Remove temp files and add real ones
      pendingFiles.forEach((file) => {
        dispatch(["removeFile", { fileId: file.id }]);
      });
      dispatch(["addQuickFormFiles", { files: uploadedFiles }]);

      // STEP 4: Detect types for each file (in parallel)
      const detectionPromises = uploadedFiles.map(async (file) => {
        try {
          const detectionResponse = await axiosContext.post(
            `${Endpoints.QuickFormDetectType}/${sessionId}/${file.id}`
          );

          const detectedType =
            FileType[
              detectionResponse.data.detectedType as keyof typeof FileType
            ];

          // Update detected type
          dispatch([
            "updateFileDetectedType",
            { fileId: file.id, detectedType },
          ]);

          // Auto-set transformTo to opposite type
          // TODO: think about moving this to a "transform rule" set
          const transformTo =
            detectedType === FileType.PROTO ? FileType.XML : FileType.PROTO;

          // Update frontend
          dispatch(["updateFileTransformTo", { fileId: file.id, transformTo }]);

          // SYNC TO BACKEND
          await axiosContext.put(
            `${Endpoints.QuickFormUpdateTransformTo}/${sessionId}/${file.id}`,
            { transformTo: transformTo.toString() }
          );
        } catch (error) {
          console.error(`Type detection failed for ${file.id}`);
          dispatch([
            "updateFileDetectedType",
            { fileId: file.id, detectedType: FileType.UNDETECTED },
          ]);
        }
      });

      await Promise.all(detectionPromises);

      loggedNotify.positive(
        axiosContext,
        Component.QUICKFORM,
        `${uploadedFiles.length} file(s) uploaded successfully`
      );
    } catch (error) {
      loggedNotify.negative(axiosContext, Component.QUICKFORM, "Upload failed");
    }
  };

  // Cleanup session on unmount
  React.useEffect(() => {
    return () => {
      axiosContext.delete(`${Endpoints.QuickFormCleanup}/${sessionId}`);
    };
  }, [sessionId, axiosContext]);

  // Add this effect after other useEffects
  React.useEffect(() => {
    // Validate all files when they change
    state.files.forEach((file) => {
      const validationResult = ruleEngine.validate(file);

      if (
        JSON.stringify(file.validationResult) !==
        JSON.stringify(validationResult)
      ) {
        dispatch([
          "updateFileValidation",
          { fileId: file.id, validationResult },
        ]);
      }
    });
  }, [state.files, dispatch]);

  // Handle file removal
  const handleRemoveFile = React.useCallback(
    async (fileId: string) => {
      dispatch(["removeFile", { fileId }]);
      loggedNotify.positive(
        axiosContext,
        Component.QUICKFORM,
        "File removed from transformation queue"
      );
    },
    [dispatch, axiosContext]
  );

  // Handle bulk delete
  const handleDeleteSelected = React.useCallback(() => {
    const selectedFiles = state.files.filter((f) => f.selected);
    selectedFiles.forEach((file) => {
      dispatch(["removeFile", { fileId: file.id }]);
    });
    loggedNotify.positive(
      axiosContext,
      Component.QUICKFORM,
      `${selectedFiles.length} file(s) removed from transformation queue`
    );
  }, [state.files, dispatch, axiosContext]);

  // Handle transform of selected files in bulk
  const handleTransformAll = React.useCallback(async () => {
    const selectedFiles = state.files.filter((f) => f.selected);

    if (selectedFiles.length === 0) {
      loggedNotify.warning(
        axiosContext,
        Component.QUICKFORM,
        "No files selected"
      );
      return;
    }

    // Filter out files that don't pass validation
    const validFiles = selectedFiles.filter(
      (f) => f.validationResult?.isValid !== false
    );
    const invalidFiles = selectedFiles.filter(
      (f) => f.validationResult?.isValid === false
    );

    if (invalidFiles.length > 0) {
      loggedNotify.warning(
        axiosContext,
        Component.QUICKFORM,
        `${invalidFiles.length} file(s) cannot be transformed due to validation errors`
      );
    }

    if (validFiles.length === 0) {
      loggedNotify.negative(
        axiosContext,
        Component.QUICKFORM,
        "No valid files to transform"
      );
      return;
    }

    try {
      validFiles.forEach((file) => {
        dispatch([
          "updateFileTransformStatus",
          { fileId: file.id, status: TransformStatus.PROCESSING },
        ]);
      });

      loggedNotify.info(
        axiosContext,
        Component.QUICKFORM,
        `Starting transformation for ${validFiles.length} file(s)...`
      );

      const response = await axiosContext.post(
        Endpoints.QuickFormTransformBulk,
        {
          fileIds: validFiles.map((f) => f.id),
        },
        {
          headers: { "X-Session-ID": sessionId },
        }
      );

      dispatch(["updateTransformResults", { results: response.data }]);

      const successCount = response.data.filter(
        (r: any) => r.transformSuccess
      ).length;

      if (successCount === 0) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "There were no successful transformations. See file row transform status for more info"
        );
      } else {
        loggedNotify.positive(
          axiosContext,
          Component.QUICKFORM,
          `${successCount} file(s) transformed successfully`
        );
      }
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.QUICKFORM,
        "Transform failed"
      );
    }
  }, [state.files, dispatch, axiosContext, sessionId]);

  // Handle detected type change
  const handleDetectedTypeChange = React.useCallback(
    async (fileId: string, type: FileType) => {
      dispatch(["updateFileDetectedType", { fileId, detectedType: type }]);

      try {
        await axiosContext.put(
          `${Endpoints.QuickFormUpdateType}/${sessionId}/${fileId}`,
          {
            detectedType: type,
          }
        );
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Failed to update file type on server"
        );
      }
    },
    [dispatch, axiosContext, sessionId]
  );

  // Handle transform to change
  const handleTransformToChange = React.useCallback(
    async (fileId: string, type: FileType | null) => {
      dispatch(["updateFileTransformTo", { fileId, transformTo: type }]);

      try {
        await axiosContext.put(
          `${Endpoints.QuickFormUpdateTransformTo}/${sessionId}/${fileId}`,
          {
            transformTo: type,
          }
        );
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Failed to update transform target on server"
        );
      }
    },
    [dispatch, axiosContext, sessionId]
  );
  // Handle bulk set detected type
  const handleBulkSetDetectedType = React.useCallback(
    async (type: FileType) => {
      const selectedFiles = state.files.filter((f) => f.selected);

      // Update all selected files' detected type in one action
      dispatch(["bulkSetDetectedType", { detectedType: type }]);

      try {
        await Promise.all(
          selectedFiles.map((file) =>
            axiosContext.put(
              `${Endpoints.QuickFormUpdateType}/${sessionId}/${file.id}`,
              {
                detectedType: type,
              }
            )
          )
        );
        loggedNotify.positive(
          axiosContext,
          Component.QUICKFORM,
          `Updated ${selectedFiles.length} file(s) detected type to ${type}`
        );
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Failed to update some files on server"
        );
      }
    },
    [state.files, dispatch, axiosContext, sessionId]
  );

  // Handle bulk set transform type
  const handleBulkSetTransformType = React.useCallback(
    async (type: FileType) => {
      const selectedFiles = state.files.filter((f) => f.selected);

      // Filter files based on what transforms are valid for the target type
      const validFiles = selectedFiles.filter((file) => {
        if (type === FileType.CPP) {
          // C++ is only valid for PROTO files
          return file.detectedType === FileType.PROTO;
        } else if (type === FileType.XML) {
          // XML transform is only valid for PROTO files
          return file.detectedType === FileType.PROTO;
        } else if (type === FileType.PROTO) {
          // PROTO transform is only valid for XML files
          return file.detectedType === FileType.XML;
        }
        return false;
      });

      if (validFiles.length === 0) {
        loggedNotify.warning(
          axiosContext,
          Component.QUICKFORM,
          `None of the selected files can be transformed to ${type}`
        );
        return;
      }

      // Update only the valid files in the reducer
      dispatch(["bulkSetTransformType", { transformType: type }]);

      try {
        // Sync only the valid files to backend
        await Promise.all(
          validFiles.map((file) =>
            axiosContext.put(
              `${Endpoints.QuickFormUpdateTransformTo}/${sessionId}/${file.id}`,
              {
                transformTo: type,
              }
            )
          )
        );

        const skippedCount = selectedFiles.length - validFiles.length;
        if (skippedCount > 0) {
          loggedNotify.info(
            axiosContext,
            Component.QUICKFORM,
            `Updated ${validFiles.length} file(s) to transform to ${type}. Skipped ${skippedCount} incompatible file(s).`
          );
        } else {
          loggedNotify.positive(
            axiosContext,
            Component.QUICKFORM,
            `Updated ${validFiles.length} file(s) to transform to ${type}`
          );
        }
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Failed to update some files on server"
        );
      }
    },
    [state.files, dispatch, axiosContext, sessionId]
  );

  // Handle download file
  const handleDownloadFile = React.useCallback(
    async (fileId: string) => {
      try {
        const response = await axiosContext.get(
          `${Endpoints.QuickFormDownload}/${sessionId}/${fileId}`,
          {
            responseType: "blob", // Important!
          }
        );

        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers["content-disposition"];
        const filename = contentDisposition
          ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
          : "transformed-file";

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        loggedNotify.positive(
          axiosContext,
          Component.QUICKFORM,
          "File downloaded successfully"
        );
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Download failed"
        );
      }
    },
    [sessionId, axiosContext]
  );

  // Handle download all as zip
  const handleDownloadAllZip = React.useCallback(async () => {
    const successfulFiles = state.files.filter(
      (f) => f.transformStatus === TransformStatus.SUCCESS
    );

    if (successfulFiles.length === 0) {
      loggedNotify.warning(
        axiosContext,
        Component.QUICKFORM,
        "No successfully transformed files to download"
      );
      return;
    }

    try {
      // Send the list of file IDs to include in the ZIP
      const response = await axiosContext.post(
        `${Endpoints.QuickFormDownloadZip}/${sessionId}`,
        {
          fileIds: successfulFiles.map((f) => f.id),
        },
        {
          responseType: "blob",
        }
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "transformed-files.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      loggedNotify.positive(
        axiosContext,
        Component.QUICKFORM,
        "Zip file downloaded successfully"
      );
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.QUICKFORM,
        "Download failed"
      );
    }
  }, [state.files, sessionId, axiosContext]);

  // Handle cancel upload
  const handleCancelUpload = React.useCallback(
    (fileId: string) => {
      dispatch([
        "updateFileUploadStatus",
        { fileId, status: UploadStatus.CANCELLED },
      ]);
      loggedNotify.info(axiosContext, Component.QUICKFORM, "Upload cancelled");
    },
    [dispatch, axiosContext]
  );

  // Handle show error details
  const handleShowErrorDetails = React.useCallback(
    (fileId: string, errorMessage: string) => {
      const file = state.files.find((f) => f.id === fileId);
      if (file) {
        setErrorModalState({
          isOpen: true,
          fileName: file.fileInfo.attributes?.name || "Unknown file",
          errorMessage,
          fileId,
        });
      }
    },
    [state.files]
  );

  // Handle retry transformation
  const handleRetryTransformation = React.useCallback(async () => {
    if (errorModalState.fileId) {
      const file = state.files.find((f) => f.id === errorModalState.fileId);

      if (!file) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "File not found"
        );
        return;
      }

      // Set to processing
      dispatch([
        "updateFileTransformStatus",
        {
          fileId: errorModalState.fileId,
          status: TransformStatus.PROCESSING,
        },
      ]);

      // Close modal
      setErrorModalState({
        isOpen: false,
        fileName: "",
        errorMessage: "",
        fileId: "",
      });

      try {
        loggedNotify.info(
          axiosContext,
          Component.QUICKFORM,
          "Retrying transformation..."
        );

        // Retry the transformation via API
        const response = await axiosContext.post(
          Endpoints.QuickFormTransformBulk,
          {
            fileIds: [errorModalState.fileId],
          },
          {
            headers: { "X-Session-ID": sessionId },
          }
        );

        dispatch(["updateTransformResults", { results: response.data }]);

        const success = response.data[0]?.transformSuccess;
        if (success) {
          loggedNotify.positive(
            axiosContext,
            Component.QUICKFORM,
            "Transformation succeeded!"
          );
        } else {
          loggedNotify.negative(
            axiosContext,
            Component.QUICKFORM,
            "Transformation failed again"
          );
        }
      } catch (error) {
        loggedNotify.negative(
          axiosContext,
          Component.QUICKFORM,
          "Retry failed"
        );
        dispatch([
          "updateFileTransformStatus",
          {
            fileId: errorModalState.fileId,
            status: TransformStatus.FAILED,
            errorMessage: "Retry failed",
          },
        ]);
      }
    }
  }, [errorModalState.fileId, state.files, dispatch, axiosContext, sessionId]);

  const selectedCount = state.files.filter((f) => f.selected).length;

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 71px)",
        backgroundColor: currentTheme.colors.pageBackgroundColor,
        overflow: "hidden",
      }}
    >
      <main
        style={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: currentTheme.colors.pageBackgroundColor,
        }}
      >
        {/* Drop Zone */}
        <QuickFormDropZone
          theme={currentTheme}
          onFilesSelected={handleUpload}
          axiosContext={axiosContext}
        />

        {/* Action Bar */}
        <QuickFormActionBar
          theme={currentTheme}
          totalFiles={state.files.length}
          selectedCount={selectedCount}
          onDeleteSelected={handleDeleteSelected}
          onTransformAll={handleTransformAll}
          onDownloadAllZip={handleDownloadAllZip}
        />

        {/* File Table */}
        <QuickFormTable
          theme={currentTheme}
          onBulkSetTransformType={handleBulkSetTransformType}
          onBulkSetDetectedType={handleBulkSetDetectedType}
          files={paginatedFiles}
          allSelected={state.allSelected}
          selectedCount={selectedCount}
          onToggleAll={() => dispatch(["toggleAllSelection"])}
          onToggleFile={(fileId) =>
            dispatch(["toggleFileSelection", { fileId }])
          }
          onDetectedTypeChange={handleDetectedTypeChange}
          onTransformToChange={handleTransformToChange}
          onRemoveFile={handleRemoveFile}
          onDownloadFile={handleDownloadFile}
          onCancelUpload={handleCancelUpload}
          onShowErrorDetails={handleShowErrorDetails}
        />

        {/* Pagination */}
        {state.files.length > 0 && (
          <QuickFormPagination
            theme={currentTheme}
            currentPage={state.currentPage}
            totalPages={totalPages}
            totalItems={state.files.length}
            itemsPerPage={state.itemsPerPage}
            onPageChange={(page) => dispatch(["setPage", { page }])}
          />
        )}

        {/* Error Modal */}
        <QuickFormErrorModal
          theme={currentTheme}
          isOpen={errorModalState.isOpen}
          fileName={errorModalState.fileName}
          errorMessage={errorModalState.errorMessage}
          onClose={() =>
            setErrorModalState({
              isOpen: false,
              fileName: "",
              errorMessage: "",
              fileId: "",
            })
          }
          onRetry={handleRetryTransformation}
        />
      </main>
    </div>
  );
};
